import { computed, observable, reaction, intercept } from 'mobx';
import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import SelectedManager from '@src/slide/application/service/selection/selected-manager';
import SelectedEleManager from '@src/slide/application/service/selection/selected-ele-manager';
import SelectedWithCommitManager from '@src/slide/application/service/selection/selected-with-commit-manager';
import { RegionType } from '@src/slide/domain/selection/constant/region';
import {
    ISelectionService,
    SelectedModelType,
} from '@src/slide/application/service/selection/selection-service.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { IMasterModel } from '@src/slide/domain/ui-model/element-modeles/Interface/master-model.interface';
import { ILayoutModel } from '@src/slide/domain/ui-model/element-modeles/Interface/layout-model.interface';
import * as mobx from 'mobx';
import MultiplyModel from '@src/slide/domain/ui-model/element-modeles/multiply-model';
import EmptyModel, { proxyHandler } from '@src/slide/domain/ui-model/element-modeles/empty-model';
import {
    IElementBaseModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import eventBus, { EventKeys } from '@src/slide/infrastructure/module/independent/event-emitter/instance';

export class SelectionService implements ISelectionService {
    /**
     * 当前选中的 slide 的类型，可能是 slide/master/layout
     */
    @observable
    public currentSlideType: UIModelType.SLIDE | UIModelType.LAYOUT | UIModelType.MASTER = UIModelType.SLIDE;
    // 当前中间编辑区 显示的slideIndex
    @observable
    public currentSlideIndex = -1;
    /**
     * 1. [PC] 变更 selectionService.currentSlideIndex，通过数据驱动更新 ui
     * 2. [mobile] 移动分为 只读 / 编辑 两个状态，其中只读态的 {当前页} 数据源有差异 selectionService.mobileCurrentDisplayIndex
     */
    @observable
    public mobileCurrentDisplayIndex = 0;
    @observable
    public needClearSelectedSlideClearanceFlag = 0;

    /**
     * 左侧幻灯片列表中选中的间隙索引
     */
    @observable
    public slideClearanceIndex = -1;

    @observable
    public lastMouseDownRegion: RegionType = RegionType.NONE;

    public selectedSlides: SelectedManager;
    public selectedElements: SelectedEleManager;
    public selectedWithCommit: SelectedWithCommitManager;
    // 阻止切页之后清除选中元素
    public preventSelectedElementsClear: boolean;

    @mobx.observable
    public selectedModel: SelectedModelType;

    /**
     * 记录母版编辑和普通编辑模式下最后一次编辑的页面索引
     */
    public lastMasterEditPageIndex = 0;
    public lastMasterEditSlideType: UIModelType.LAYOUT | UIModelType.MASTER = UIModelType.MASTER;
    public lastSlideEditPageIndex = 0;
    /**
     * 所依赖的UIDataService
     * @private
     */
    public uiDataService: IUiDataService;

    public constructor(uiDataService: IUiDataService) {
        this.uiDataService = uiDataService;
        this.selectedSlides = new SelectedManager();
        this.selectedElements = new SelectedEleManager(this);
        this.selectedWithCommit = new SelectedWithCommitManager(this);
        this.preventSelectedElementsClear  = false;

        this.registerCurrentSlideChange();
        this.observeSelectedElements();
        this.interceptCurrentSlideIndexChange();
    }

    @computed
    public get currentSlide(): ISlideModel | IMasterModel | ILayoutModel | undefined {
        return this.slides?.[this.currentSlideIndex];
    }

    /**
     * @deprecated
     * 代指 currentList。当前展示/编辑的列表数据，可能是 slides 或 masterLayout
     * 如果是为了取 slides，建议使用 slideModelList
     * 如果是为了取 masters 建议使用 masterModelList
     * 如果是为了取 layouts 建议使用 layoutModelList
     */
    @computed public get slides(): Array<ISlideModel | IMasterModel | ILayoutModel> {
        switch (this.currentSlideType) {
            case UIModelType.MASTER:
                return this.uiDataService.masterModelList;
            case UIModelType.LAYOUT:
                return this.uiDataService.layoutModelList;
            default:
                return this.uiDataService.slideModelList;
        }
    }

    public clearAll() {
        this.selectedSlides.clear();
        this.selectedElements.clear();
    }

    public getSelectedModel(): SelectedModelType {
        return this.selectedModel;
    }


    public updateCurrentSlideType(type) {
        this.currentSlideType = type || UIModelType.SLIDE;
    }

    /**
     * 用户行为触发的 currentSlideIndex 变更，讲派发事件，目的是区分 主动变更 or 被动变更
     * index 有效性不做校验，由使用方保证
     */
    public changeCurrentSlideIndexByUserAction(index: number) {
        if (typeof index !== 'number') {
            return;
        }
        this.currentSlideIndex = index;
        window.dispatchEvent(new CustomEvent('onCurrentSlideIndexChangeByUserAction'));
    }

    private registerCurrentSlideChange(): void {
        // slide变更才去clear选中，而不是索引变更
        reaction(() => this.currentSlide, () => {
            if (this.preventSelectedElementsClear) {
                this.preventSelectedElementsClear = false;
            } else {
                this.selectedElements.clear();
            }
        });
    }

    private observeSelectedElements() {
        mobx.reaction(() => (
            {
                selectedElementsCount: this.selectedElements.length,
                selectedRegion: this.lastMouseDownRegion,
            }
        ), (selectedData) => {
            // 释放之前创建的多选对象
            if (this.selectedModel instanceof MultiplyModel) {
                this.selectedModel.dispose?.();
            }

            let model: SelectedModelType = null;
            // 当前在note区的话，返回note，此处逻辑与qiyuan讨论过，后续selectedManager统一后也应当统一
            if (selectedData.selectedRegion === RegionType.NOTE
                && this.currentSlide) {
                model = this.currentSlide?.noteData;
            } else {
                const len = this.selectedElements.length;
                if (len === 0) {
                    model = new EmptyModel({});
                    model = new Proxy(model, proxyHandler);
                } else if (len === 1) {
                    model = this.selectedElements.selectedArray?.[0];
                } else {
                    const selectedModels: Array<IElementBaseModel> = [];
                    this.selectedElements.selectedArray?.forEach((model) => {
                        selectedModels.push(model);
                    });
                    model = new MultiplyModel({ selectedModels,
                        selectionService: this,
                    });
                }
            }
            this.selectedModel = model || new EmptyModel({});
        }, { fireImmediately: true });
    }

    // 拦截currentSlideIndex的变更，在一系列reaction前打标
    private interceptCurrentSlideIndexChange() {
        intercept(this, 'currentSlideIndex', (change) => {
            const { newValue } = change;
            if (newValue !== this.currentSlideIndex) {
                eventBus.emit(EventKeys.SLIDE_CHANGE_INDEX_BEFORE, newValue);
            }
            return change;
        });
    }
}
